import { apiAxiosInstance } from '@/lib/api/config';
import type { AxiosError } from 'axios';

/**
 * Error codes returned by backend for client switching authorization failures
 */
const CLIENT_AUTH_ERROR_CODES = [
  'INVALID_CLIENT_ACCESS', // User doesn't have access to the specified client
  'CLIENT_HEADER_REQUIRED', // Deprecated: Kept for backward compatibility with older backend versions
  'ACTIVE_CLIENT_REQUIRED', // X-Active-Client-Id header is required for Client role users
  'INVALID_CLIENT_GUID', // Header value is not a valid GUID format
] as const;

/**
 * Custom event dispatched when client authorization fails
 * Listeners can show error messages and handle navigation
 */
export interface ClientAuthErrorEvent extends CustomEvent {
  detail: {
    errorCode: string;
    errorMessage: string;
    invalidClientId?: string;
  };
}

/**
 * Axios response interceptor to handle client authorization errors
 * Detects 400/401 errors related to client switching and dispatches custom events
 *
 * Error Handling Strategy:
 * - Clear invalid client selection from sessionStorage
 * - Dispatch custom event for UI components to show error messages
 * - Let components handle navigation (e.g., redirect to /client-select)
 */
export function setupClientAuthErrorInterceptor(): void {
  apiAxiosInstance.interceptors.response.use(
    (response) => response,
    (error: AxiosError<{ error?: { code?: string; message?: string } }>) => {
      // Check if it's a client authorization error (400 or 401)
      if (error.response?.status === 400 || error.response?.status === 401) {
        const errorCode = error.response?.data?.error?.code;
        const errorMessage = error.response?.data?.error?.message;

        // Check if the error is related to client switching
        if (errorCode && CLIENT_AUTH_ERROR_CODES.includes(errorCode as (typeof CLIENT_AUTH_ERROR_CODES)[number])) {
          // Compare the client ID that was sent in the request with what's currently in sessionStorage.
          // If they differ, this error is from a stale request (e.g., fired before the user selected a client)
          // and should NOT clear the current valid selection.
          const requestClientId = error.config?.headers?.['X-Active-Client-Id'] as string | undefined;
          const currentStoredId = sessionStorage.getItem('selectedClientId');

          if (currentStoredId && requestClientId !== currentStoredId) {
            // Stale request — a client has been selected (or switched) since this request was made.
            // Do not clear storage or redirect; just let the error propagate normally.
            return Promise.reject(error);
          }

          // Get the invalid client ID before clearing it
          const selectedClientJson = sessionStorage.getItem('selectedClient');
          let invalidClientId: string | undefined;

          if (selectedClientJson) {
            try {
              const selectedClient = JSON.parse(selectedClientJson);
              invalidClientId = selectedClient?.id;
            } catch {
              // Ignore parse errors
            }
          }

          // Clear invalid client selection
          sessionStorage.removeItem('selectedClient');
          sessionStorage.removeItem('selectedClientId');

          // Dispatch custom event for UI components to handle
          window.dispatchEvent(
            new CustomEvent('client:auth-error', {
              detail: {
                errorCode,
                errorMessage: errorMessage || 'Client authorization failed',
                invalidClientId,
              },
            }) as ClientAuthErrorEvent
          );
        }
      }

      return Promise.reject(error);
    }
  );
}
